"use client";

import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { usePathname } from 'next/navigation';
import { useConversation } from '@elevenlabs/react';
import type {
  AgentToolResponseClientEvent,
} from '@elevenlabs/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Mic, Phone, X, Volume2, Send } from 'lucide-react';
import { toast } from '@/lib/toast';
import { getVoiceAgentConversationTokenAction } from '@/api/voice-agent';
import { WidgetTheme, DEFAULT_THEME, getWidgetTextContent } from '@/shared/theme';
import { useLanguage } from '@/components/providers/language-provider';
import agentsData from '@/json/agents.json';

interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

interface AgentVariantConfig {
  id: string;
  name: string;
  agentId: string;
  language: string;
}

interface AgentChoice {
  id: string;
  label: string;
  description: string;
  badge: string;
  gradient: string;
}

const isAgentVariantConfig = (value: unknown): value is AgentVariantConfig => (
  typeof value === 'object' &&
  value !== null &&
  'agentId' in value &&
  'id' in value &&
  'name' in value &&
  'language' in value
);

const formatVariantLabel = (variantKey: string): string =>
  variantKey
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

interface FloatingVoiceAgentProps {
  agentId: string;
  agentName?: string;
  userId?: string;
  onConversationStart?: (conversationId: string) => void;
  onConversationEnd?: () => void;
  className?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  inlineMode?: boolean;
  theme?: WidgetTheme;
  embedded?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onConnecting?: () => void;
  defaultWidgetMode?: WidgetMode;
  hideLauncherButton?: boolean;
}

export interface FloatingVoiceAgentRef {
  endConversation: () => Promise<void>;
  isConnected: boolean;
}

type WidgetMode = 'collapsed' | 'options' | 'voice' | 'mic-error';
type VoiceState = 'idle' | 'connecting' | 'listening' | 'speaking' | 'thinking';

type SystemMessageKey = 'userEnded' | 'agentEnded';

interface ConversationMessage {
  message: string;
  source: 'ai' | 'user' | 'system';
  messageKey?: SystemMessageKey;
}

const FloatingVoiceAgentComponent = forwardRef<FloatingVoiceAgentRef, FloatingVoiceAgentProps>(({
  agentId,
  agentName = '',
  userId,
  onConversationStart,
  onConversationEnd,
  className = '',
  position = 'bottom-right',
  inlineMode = false,
  theme,
  embedded = false,
  onConnect = () => {},
  onDisconnect = () => {},
  onConnecting = () => {},
  defaultWidgetMode,
  hideLauncherButton = false,
}, ref) => {
  const { language, direction } = useLanguage();
  const isRTL = direction === 'rtl';
  const pathname = usePathname();
  const isWidgetRoute = pathname?.includes('/widget') ?? false;
  console.log('language: ', language);

  const resolvedTextContent = useMemo(() => {
    const baseContent = getWidgetTextContent(language);
    if (theme?.textContent) {
      return { ...baseContent, ...theme.textContent };
    }
    return baseContent;
  }, [language, theme?.textContent]);

  const resolvedConfig = useMemo(() => theme?.config || DEFAULT_THEME, [theme]);

  // Use provided theme overrides or defaults - memoized to prevent recreation
  const widgetTheme = useMemo(() => ({
    config: resolvedConfig,
    textContent: resolvedTextContent,
  }), [resolvedConfig, resolvedTextContent]);
  
  // Build agent choices from agents.json for dropdown
  const agentChoices = useMemo<AgentChoice[]>(() => {
    const appointmentAgents = (agentsData as any).Use_Case_Appointment_Agent;
    if (!appointmentAgents) {
      return [];
    }

    return Object.entries(appointmentAgents)
      .filter(([key, value]) => key !== 'displayName' && isAgentVariantConfig(value))
      .map(([variantKey, variantValue]) => {
        const variant = variantValue as AgentVariantConfig;
        return {
          id: variant.agentId,
          label: formatVariantLabel(variantKey),
          description: `${variant.language} voice`,
          badge: variant.language.slice(0, 2).toUpperCase(),
          gradient: 'from-[#8d6dd5] to-[#5f2fd0]',
        };
      });
  }, []);

  // State for agent selection (only used when dropdown is shown)
  const [currentAgentId, setCurrentAgentId] = useState(agentId);
  const [currentAgentName, setCurrentAgentName] = useState(agentName);
  // Track if agent was initially provided (to hide dropdown when URL has gender param)
  const [wasAgentProvidedInitially, setWasAgentProvidedInitially] = useState(!!agentId);

  // Update state when props change (but not when user has selected from dropdown)
  useEffect(() => {
    // Only update if currentAgentId is empty (initial state) or if agentId prop changes
    if (!currentAgentId && agentId) {
      setCurrentAgentId(agentId);
      setCurrentAgentName(agentName);
      setWasAgentProvidedInitially(true);
    }
  }, [agentId, agentName, currentAgentId]);
  // Inject CSS variables for theme
  useEffect(() => {
    const root = document.documentElement;
    const colors = widgetTheme.config.colors;
    const spacing = widgetTheme.config.spacing;
    const typography = widgetTheme.config.typography;

    // Set color variables
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--widget-color-${key.replace(/_/g, '-')}`, value);
    });

    // Set spacing variables
    Object.entries(spacing).forEach(([key, value]) => {
      root.style.setProperty(`--widget-spacing-${key.replace(/_/g, '-')}`, `${value}px`);
    });

    // Set typography variables
    root.style.setProperty('--widget-font-family', typography.font_family);
    root.style.setProperty('--widget-font-size-small', `${typography.font_size_small}px`);
    root.style.setProperty('--widget-font-size-medium', `${typography.font_size_medium}px`);
    root.style.setProperty('--widget-font-size-large', `${typography.font_size_large}px`);
    root.style.setProperty('--widget-font-weight-normal', typography.font_weight_normal.toString());
    root.style.setProperty('--widget-font-weight-medium', typography.font_weight_medium.toString());
    root.style.setProperty('--widget-font-weight-bold', typography.font_weight_bold.toString());

    return () => {
      // Cleanup on unmount
      Object.keys(colors).forEach((key) => {
        root.style.removeProperty(`--widget-color-${key.replace(/_/g, '-')}`);
      });
      Object.keys(spacing).forEach((key) => {
        root.style.removeProperty(`--widget-spacing-${key.replace(/_/g, '-')}`);
      });
      root.style.removeProperty('--widget-font-family');
      root.style.removeProperty('--widget-font-size-small');
      root.style.removeProperty('--widget-font-size-medium');
      root.style.removeProperty('--widget-font-size-large');
      root.style.removeProperty('--widget-font-weight-normal');
      root.style.removeProperty('--widget-font-weight-medium');
      root.style.removeProperty('--widget-font-weight-bold');
    };
  }, [widgetTheme]);

  const initialWidgetMode = useMemo<WidgetMode>(
    () => {
      if (hideLauncherButton) {
        return 'options';
      }
      return defaultWidgetMode ?? (inlineMode ? 'options' : 'collapsed');
    },
    [defaultWidgetMode, inlineMode, hideLauncherButton]
  );
  const [widgetMode, setWidgetMode] = useState<WidgetMode>(initialWidgetMode);
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [micPermissionError, setMicPermissionError] = useState<string | null>(null);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const [audioDevices, setAudioDevices] = useState<AudioDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [outputDevices, setOutputDevices] = useState<AudioDevice[]>([]);
  const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState<string>('');
  // Refs for auto-scrolling
  const voiceMessagesRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const isWidgetOpenRef = useRef<boolean>(false);
  const conversationRef = useRef<any>(null);
  const manualEndRef = useRef<boolean>(false);
  const isStartingRef = useRef<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);

  const agentEndedRef = useRef<boolean>(false);
  const hasAttemptedStartRef = useRef<boolean>(false);
  const hasShownErrorToastRef = useRef<boolean>(false);

  const classifyMicError = useCallback(
    (error?: any) => {
      const errorName = error?.name;
      const errorMessage =
        typeof error?.message === 'string' ? error.message.toLowerCase() : '';
      const unavailable =
        errorName === 'NotFoundError' ||
        errorName === 'DevicesNotFoundError' ||
        errorName === 'NotReadableError' ||
        errorMessage.includes('no audio tracks') ||
        errorMessage.includes('no audio device') ||
        errorMessage.includes('could not start audio source');

      if (unavailable) {
        return {
          denied: false,
          message: widgetTheme.textContent.microphone_unavailable_message,
        };
      }

      return {
        denied: true,
        message: widgetTheme.textContent.microphone_blocked_message,
      };
    },
    [
      widgetTheme.textContent.microphone_blocked_message,
      widgetTheme.textContent.microphone_unavailable_message,
    ]
  );

  const micErrorShortMessage = useMemo(
    () =>
      micPermissionDenied
        ? widgetTheme.textContent.microphone_access_required
        : widgetTheme.textContent.microphone_unavailable_title,
    [
      micPermissionDenied,
      widgetTheme.textContent.microphone_access_required,
      widgetTheme.textContent.microphone_unavailable_title,
    ]
  );

  const micErrorLongMessage = useMemo(
    () =>
      micPermissionDenied
        ? widgetTheme.textContent.microphone_blocked_message
        : widgetTheme.textContent.microphone_unavailable_message,
    [
      micPermissionDenied,
      widgetTheme.textContent.microphone_blocked_message,
      widgetTheme.textContent.microphone_unavailable_message,
    ]
  );

  const greetingMessage = useMemo(() => {
    if (currentAgentName) {
      return widgetTheme.textContent.greeting_with_agent.replace('{{agentName}}', currentAgentName);
    }
    return widgetTheme.textContent.greeting_without_agent;
  }, [currentAgentName, widgetTheme.textContent.greeting_with_agent, widgetTheme.textContent.greeting_without_agent]);

  // Notify parent window when widget state changes (for embedded iframe)
  useEffect(() => {
    if (embedded && typeof window !== 'undefined' && window.parent !== window) {
      window.parent.postMessage({
        type: 'techfyte-widget-state',
        state: widgetMode === 'collapsed' ? 'collapsed' : 'expanded'
      }, '*');
    }
  }, [widgetMode, embedded]);

  // Auto-start voice call when in inline mode (but not after manual end)
  useEffect(() => {
    if (inlineMode && widgetMode === 'options' && !isConnected && !isLoading && !manualEndRef.current && !micPermissionDenied && !hasAttemptedStartRef.current && currentAgentId) {
      // Start the conversation in voice mode for inline
      isWidgetOpenRef.current = true;
      hasAttemptedStartRef.current = true;
      hasShownErrorToastRef.current = false; // Reset error toast flag for new attempt
      startConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineMode, widgetMode, isConnected, isLoading, micPermissionDenied, currentAgentId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const scrollToBottom = (ref: React.RefObject<HTMLDivElement>) => {
      if (ref.current) {
        ref.current.scrollTop = ref.current.scrollHeight;
      }
    };

    scrollToBottom(voiceMessagesRef);
  }, [messages]);

  // Track mobile breakpoint for responsive behavior
  useEffect(() => {
    const check = () => {
      try {
        setIsMobile(window.innerWidth <= 640); // Tailwind's sm breakpoint ~640px
      } catch (e) {
        setIsMobile(false);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const { status, ...conversation } = useConversation({
    micMuted: false, // Always muted initially, will be controlled per mode
    volume: 1, // Disable audio output initially
    onMicMutedChange: useCallback((muted: boolean) => {
      console.log('Microphone muted state changed:', muted);
      if (muted && isConnected) {
        // If mic gets muted during voice call, show warning
        toast.error(
          widgetTheme.textContent.microphone_muted_title,
          widgetTheme.textContent.microphone_muted_message
        );
      }
    }, [isConnected, widgetTheme.textContent.microphone_muted_message, widgetTheme.textContent.microphone_muted_title]),
    onConnect: useCallback(() => {
      console.log('Connected to voice agent');
      // If the widget has been closed while connecting, immediately end the session
      if (!isWidgetOpenRef.current) {
        console.warn('Connection completed but widget is closed - ending session');
        try {
          conversationRef.current?.stopSpeech?.();
          conversationRef.current?.endSession?.();
        } catch (e) {
          console.warn('Failed to end session after late connect', e);
        }
        return;
      }
      setIsConnected(true);
      setIsLoading(false);
      setVoiceState('listening');
      setError(null);
      isStartingRef.current = false;
      onConnect?.();
    }, [onConnect]),
    
    onDisconnect: useCallback(() => {
      console.log('Disconnected from voice agent');
      setIsConnected(false);
      setVoiceState('idle');
      setIsLoading(false);
      
      // Add conversation end message
      const endMessage: ConversationMessage = {
        message: '',
        source: 'system',
        messageKey: agentEndedRef?.current ? 'agentEnded' : 'userEnded',
      };
      setMessages(prev => [...prev, endMessage]);
      onDisconnect?.();
      onConversationEnd?.();
    }, [onConversationEnd, onDisconnect]),

    onMessage: (message: {
      message: string;
      source: 'ai' | 'user';
    }) => {
      console.log('Received message:', message);
      // If the widget is closed, ignore messages and try to stop any speech
      if (!isWidgetOpenRef.current) {
        try {
          conversationRef.current?.stopSpeech?.();
        } catch (e) {
          console.warn('Failed to stop speech on message when widget closed', e);
        }
        return;
      }
      // Set the message object directly in state
      setMessages(prev => [...prev, { message: message.message, source: message.source }]);
    },

    onError: useCallback((error: any) => {
      console.error('Voice agent error:', error);
      setError(error.message || widgetTheme.textContent.error_occurred);
      setVoiceState('idle');
      setIsLoading(false);
      setIsConnected(false);
      toast.error(
        widgetTheme.textContent.connection_error_title,
        error.message || widgetTheme.textContent.connection_error_message
      );
    }, [
      widgetTheme.textContent.connection_error_message,
      widgetTheme.textContent.connection_error_title,
      widgetTheme.textContent.error_occurred,
    ]),

    onModeChange: useCallback((modeData: { mode: string }) => {
      console.log('Mode changed:', modeData);
      if (!isWidgetOpenRef.current) {
        try {
          conversationRef.current?.stopSpeech?.();
        } catch (e) {
          console.warn('Failed to stop speech on mode change when widget closed', e);
        }
        return;
      }
      // Update voice state based on mode
      switch (modeData.mode) {
        case 'listening':
          setVoiceState('listening');
          break;
        case 'speaking':
          setVoiceState('speaking');
          break;
        case 'thinking':
          setVoiceState('thinking');
          break;
        case 'idle':
        default:
          setVoiceState('idle');
          break;
      }
    }, []),

    onStatusChange: useCallback((prop: { status: any }) => {
      console.log('Status changed:', prop.status);
      if (prop.status === 'listening') {
        setVoiceState('listening');
      } else if (prop.status === 'speaking') {
        setVoiceState('speaking');
      }
    }, []),
    onAgentToolResponse: (toolResponse: AgentToolResponseClientEvent['agent_tool_response']) => {
      console.log('Agent tool response:', toolResponse);
      // Check if this is an end_call event
      if (toolResponse && (toolResponse as any)?.tool_name === 'end_call') {
        console.log('Received end_call event, ending conversation...');
        agentEndedRef.current = true;
        endConversation();
      }
    }
  });
  console.log('status: ', status);


  // Keep a ref to the conversation object so callbacks can access it without adding it to deps
  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  const startConversation = async (initialMessage?: string, overrideAgentId?: string) => {
    // Use overrideAgentId if provided, otherwise use the agent id from props
    const agentIdToUse = overrideAgentId || currentAgentId;

    if (!agentIdToUse) {
      // Don't show error if dropdown is available - just wait for user to select
      if (agentChoices.length > 0) {
        setWidgetMode('options');
        return;
      }
      toast.error(
        widgetTheme.textContent.connection_failed_title,
        'Voice agent is not configured. Please provide a valid agent.'
      );
      setWidgetMode('options');
      return;
    }


    // Prevent concurrent starts
    if (isStartingRef.current) {
      console.log('Conversation already starting, skipping...');
      return;
    }

    // mark widget as open when starting a conversation
    isWidgetOpenRef.current = true;
    // Reset manual end flag when starting a new conversation (manual or auto)
    manualEndRef.current = false;
    isStartingRef.current = true;
    onConnecting?.();
    let response: any = null;

    try {
      setVoiceState('connecting');
      setIsLoading(true);
      setError(null);
      setMessages([]); // Clear previous messages

      console.log('startConversation - voice mode, initialMessage:', initialMessage);
      console.log('Using agentId:', agentIdToUse);

      // Get conversation token from voice agent service first with timeout
      const tokenPromise = getVoiceAgentConversationTokenAction({
        agentId: agentIdToUse,
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout - please check your connection and try again')), 10000)
      );

      response = await Promise.race([tokenPromise, timeoutPromise]) as any;
      console.log('response: ', response);

      // Check if result has error structure (from createApiErrorResponse)
      if (response && 'status' in response && response.status === false) {
        throw new Error(response.message || 'Failed to get conversation token');
      }

      // Handle success response - API returns { statusCode, status, message, type, data: { token } }
      if (!response || !response.data || !response.data.token) {
        throw new Error('Failed to get conversation token');
      }

      // Request microphone permission (always for voice mode)
      console.log('Requesting microphone permission');
      setMicPermissionError(null);
      setMicPermissionDenied(false);
      try {
        // Build audio constraints with device selection
        const audioConstraints: any = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          sampleSize: 16,
          channelCount: 1
        };

        // Add device ID if selected
        if (selectedDeviceId) {
          audioConstraints.deviceId = selectedDeviceId;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints
        });
        console.log('Microphone permission granted, stream:', stream);

        // Check if we actually got audio tracks
        const audioTracks = stream.getAudioTracks();
        console.log('Audio tracks:', audioTracks);
        if (audioTracks.length === 0) {
          throw new Error('No audio tracks available');
        }

        // Log device info for debugging
        audioTracks.forEach((track, index) => {
          console.log(`Audio track ${index}:`, {
            label: track.label,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            settings: track.getSettings()
          });
        });

        // Stop the stream immediately as we just needed permission
        stream.getTracks().forEach(track => track.stop());
      } catch (error: any) {
        console.error('Microphone permission denied:', error);
        const micError = classifyMicError(error);
        setMicPermissionDenied(micError.denied);
        setMicPermissionError(micError.message);
        setVoiceState('idle');
        setIsLoading(false);
        isStartingRef.current = false;
        onDisconnect?.();
        return; // Stop the conversation start process
      }

      console.log('response.data.token: ', response.data.token);

      // Start the conversation with voice connection
      const startSessionPromise = conversation.startSession({
        conversationToken: response.data.token,
        connectionType: 'webrtc',
      });

      console.log('Starting voice conversation');
      await startSessionPromise;

      setConversationId(Date.now().toString());
      onConversationStart?.(Date.now().toString());

    } catch (error: any) {
      console.log('Failed to start conversation:', error);
      setError(error.message || 'Failed to start conversation');
      setVoiceState('idle');
      setIsLoading(false);
      setIsConnected(false);
      isStartingRef.current = false;
      // Call onDisconnect when conversation fails
      onDisconnect?.();
      // Only show toast once - prevent duplicate toasts from multiple re-renders/attempts
      if (!hasShownErrorToastRef.current) {
        hasShownErrorToastRef.current = true;
        toast.error(
          widgetTheme.textContent.connection_failed_title,
          error.message || widgetTheme.textContent.connection_failed_message
        );
      }
    }
  };

  const endConversation = useCallback(async () => {
    try {
      console.log('Ending conversation...');
      setIsLoading(true);
      // mark widget not open so any late events know to ignore
      isWidgetOpenRef.current = false;
      // Mark as manually ended to prevent auto-reconnect
      manualEndRef.current = true;
      // Reset attempt flags when manually ending
      hasAttemptedStartRef.current = false;
      hasShownErrorToastRef.current = false;
      await conversation?.endSession?.();
      setVoiceState('idle');
      setIsLoading(false);
      setConversationId(null);
      setChatMessage('');
      setIsConnected(false);

      // Reset agent selection to show dropdown again if dropdown is available
      if (agentChoices.length > 0) {
        setCurrentAgentId('');
        setCurrentAgentName('');
        setWasAgentProvidedInitially(false);
      }

      onConversationEnd?.();

      // Keep widget open after ending conversation (especially to show dropdown)
      // Only collapse if not in inline mode, not embedded, and hideLauncherButton is false
      if (agentChoices.length > 0 || embedded || inlineMode || hideLauncherButton) {
        setWidgetMode('options');
      } else if (!inlineMode && !hideLauncherButton) {
        setWidgetMode('collapsed');
      } else {
        setWidgetMode('options');
      }
    } catch (error: any) {
      console.error('Failed to end conversation:', error);
      // Mark as manually ended to prevent auto-reconnect
      manualEndRef.current = true;
      // Reset attempt flags when manually ending
      hasAttemptedStartRef.current = false;
      hasShownErrorToastRef.current = false;
      // Still update state even if endSession fails
      setVoiceState('idle');
      setIsLoading(false);
      setConversationId(null);
      setChatMessage('');
      setIsConnected(false);

      // Reset agent selection to show dropdown again if dropdown is available
      if (agentChoices.length > 0) {
        setCurrentAgentId('');
        setCurrentAgentName('');
        setWasAgentProvidedInitially(false);
      }

      onConversationEnd?.();

      // Keep widget open after ending conversation (especially to show dropdown)
      // Only collapse if not in inline mode, not embedded, and hideLauncherButton is false
      if (agentChoices.length > 0 || embedded || inlineMode || hideLauncherButton) {
        setWidgetMode('options');
      } else if (!inlineMode && !hideLauncherButton) {
        setWidgetMode('collapsed');
      } else {
        setWidgetMode('options');
      }
    }
  }, [conversation, hideLauncherButton, inlineMode, onConversationEnd, agentChoices, embedded]);

  // Expose endConversation method via ref
  useImperativeHandle(ref, () => ({
    endConversation,
    isConnected,
  }), [endConversation, isConnected]);

  // Try to request microphone permission (prompts user where possible) and start the conversation
  const requestMicAndStart = async () => {
    try {
      setMicPermissionError(null);
      setMicPermissionDenied(false);
      // Try requesting mic permission directly — this will prompt the browser if possible
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // stop tracks immediately; we only wanted the permission
      stream.getTracks().forEach(t => { try { t.stop(); } catch (e) { } });
      // Clear any mic errors and start the normal flow
      setMicPermissionError(null);
      setMicPermissionDenied(false);
      setWidgetMode('options');
      await startConversation();
    } catch (err: any) {
      console.error('requestMicAndStart failed', err);
      const micError = classifyMicError(err);
      setMicPermissionDenied(micError.denied);
      setMicPermissionError(micError.message);
      isStartingRef.current = false;
    }
  };

  const handleMicPermissionRetry = () => {
    // Always attempt to request permission again; if the browser blocks prompt due to prior denial
    // the request will fail and we will show guidance to the user.
    requestMicAndStart();
  };

  const handleAgentSelection = useCallback(async (selectedAgentId: string) => {
    const selectedAgent = agentChoices.find(choice => choice.id === selectedAgentId);
    if (!selectedAgent) return;

    setCurrentAgentId(selectedAgentId);
    setCurrentAgentName(selectedAgent.label);
    setMessages([]);

    // Automatically start the conversation when agent is selected
    await startConversation(undefined, selectedAgentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentChoices]);

  const positionClasses = useMemo(() => {
    switch (position) {
      case 'bottom-right':
        return 'bottom-4 right-1';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-right':
        return 'top-4 right-4';
      case 'top-left':
        return 'top-4 left-4';
      default:
        return 'bottom-4 right-4';
    }
  }, [position]);

  const isCallDisabled = voiceState === 'idle' && !currentAgentId;

  // Handler functions
  const handleCallClick = () => {
    // Prepare to open for voice
    isWidgetOpenRef.current = true;

    // If the Permissions API is available, check the microphone permission state and
    // show the dedicated mic-error screen immediately when it's explicitly denied.
    try {
      const permissions = (navigator as any).permissions;
      if (permissions && typeof permissions.query === 'function') {
        permissions.query({ name: 'microphone' as any }).then((permStatus: any) => {
          if (permStatus.state === 'denied') {
            setMicPermissionDenied(true);
            setMicPermissionError(widgetTheme.textContent.microphone_blocked_message);
            setWidgetMode('mic-error');
            return;
          }

          // Not denied (granted or prompt) — open options and start conversation which will
          // request permission if needed.
          setWidgetMode('options');
          startConversation();
        }).catch(() => {
          // If permissions API fails, fall back to opening options and let startConversation handle it
          setWidgetMode('options');
          startConversation();
        });
      } else {
        // Permissions API not available — proceed and let startConversation handle permission prompts
        setWidgetMode('options');
        startConversation();
      }
    } catch (e) {
      // Any unexpected error — fallback to default flow
      setWidgetMode('options');
      startConversation();
    }
  };


  const handleCollapse = async () => {
    console.log('isConnected', isConnected)
    // Perform a full cleanup: stop speech, end sessions, stop audio elements, clear timers and UI state
    const performFullCleanup = async () => {
      try {
        // Mark widget as closed so any late events will be ignored
        isWidgetOpenRef.current = false;

        // Clear any typing timer
        if (typingTimeoutRef.current) {
          window.clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }

        // Stop any speech from the conversation SDK (best-effort)
        try {
          conversationRef.current?.stopSpeech?.();
        } catch (e) {
          console.warn('Failed to stop speech during cleanup', e);
        }

        // End the session if possible
        try {
          await conversationRef.current?.endSession?.();
        } catch (e) {
          console.warn('Failed to end session during cleanup', e);
        }

        // Pause and remove any audio elements that might be playing TTS
        try {
          const audios = Array.from(document.querySelectorAll('audio')) as HTMLAudioElement[];
          audios.forEach((a) => {
            try {
              a.pause();
              // Stop tracks if srcObject is a MediaStream
              const so: any = (a as any).srcObject;
              if (so && typeof so.getTracks === 'function') {
                so.getTracks().forEach((t: any) => { try { t.stop(); } catch (e) { } });
              }
              a.removeAttribute('src');
              try { a.srcObject = null; } catch (e) { }
              // Try to remove the element from DOM to be safe
              a.parentElement?.removeChild(a);
            } catch (e) {
              console.warn('Failed to cleanup audio element', e);
            }
          });
        } catch (e) {
          console.warn('Audio cleanup failed', e);
        }

        // Reset UI state
        setIsLoading(false);
        setIsConnected(false);
        setVoiceState('idle');
        setConversationId(null);
        setChatMessage('');
        setMessages([]);
        setMicPermissionError(null);
        setMicPermissionDenied(false);
        isStartingRef.current = false;
      } catch (e) {
        console.warn('Full cleanup failed', e);
      }
    };

    await performFullCleanup();
    if (hideLauncherButton) {
      setWidgetMode('options');
    } else {
      setWidgetMode('collapsed');
    }
  };

  const getStatusText = () => {
    if (micPermissionError) {
      return micPermissionDenied
        ? widgetTheme.textContent.microphone_access_required
        : widgetTheme.textContent.microphone_unavailable_title;
    }

    switch (voiceState) {
      case 'connecting':
        return widgetTheme.textContent.connecting_status;
      case 'listening':
        return widgetTheme.textContent.listening_status;
      case 'speaking':
        return widgetTheme.textContent.speaking_status;
      case 'thinking':
        return widgetTheme.textContent.chatting_status;
      default:
        return widgetTheme.textContent.main_label;
    }
  };

  const handleSendMessage = async () => {
    if (chatMessage.trim() && isConnected) {
      const messageText = chatMessage.trim();

      // Add user message to our state
      const userMessage: ConversationMessage = {
        message: messageText,
        source: 'user'
      };
      setMessages(prev => [...prev, userMessage]);

      // Clear the input immediately
      setChatMessage('');

      // Send the message through the conversation
      try {
        if (typeof conversation.sendUserMessage === 'function') {
          await conversation.sendUserMessage(messageText);
        } else {
          console.log('Text messaging not supported');
          toast.error(
            widgetTheme.textContent.send_failed_title,
            widgetTheme.textContent.send_not_supported_message
          );
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        toast.error(
          widgetTheme.textContent.send_failed_title,
          widgetTheme.textContent.send_failed_generic_message
        );
      }
    }
  };

  // Send user activity to indicate user is present and prevent "Are you there?" messages
  // When debounce is true, schedules activity on typing and after debounce period
  // When debounce is false, sends immediate activity (e.g., on input focus)
  // stopSpeech should only be called when user actively sends a message, not on focus/typing
  const handleUserActivity = useCallback((debounce: boolean = true) => {
    try {
      const sdk: any = conversation;

      if (debounce) {
        // Clear any existing timer
        if (typingTimeoutRef.current) {
          window.clearTimeout(typingTimeoutRef.current);
        }

        // Immediately notify that the user is typing
        sdk?.sendUserActivity?.();

        // Schedule final activity after debounce period
        typingTimeoutRef.current = window.setTimeout(() => {
          sdk?.sendUserActivity?.();
          typingTimeoutRef.current = null;
        }, 2000);
      } else {
        // Immediate activity (e.g., on input focus) - just notify presence, don't interrupt
        sdk?.sendUserActivity?.();
      }
    } catch (e) {
      console.warn('handleUserActivity failed', e);
    }
  }, [conversation]);

  // Helper function to parse URLs in message text and convert them to anchor tags
  const parseMessageWithLinks = (text: string) => {
    if (!text) return "";
    // URL regex pattern
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      // Check if this part is a URL by creating a new regex test for each part
      const isUrl = /^https?:\/\/[^\s]+$/.test(part);
      if (isUrl) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 underline hover:text-blue-300 break-all"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const resolveMessageText = useCallback((msg: ConversationMessage) => {
    if (msg.messageKey === 'userEnded') {
      return widgetTheme.textContent.user_ended_conversation;
    }
    if (msg.messageKey === 'agentEnded') {
      return widgetTheme.textContent.agent_ended_conversation;
    }
    return msg.message;
  }, [widgetTheme.textContent.agent_ended_conversation, widgetTheme.textContent.user_ended_conversation]);

  // Fetch available audio devices (both input and output)
  const fetchAudioDevices = useCallback(async () => {
    try {
      // Request permission first to get proper device labels
      let tempStream: MediaStream | null = null;
      try {
        tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the stream immediately - we just needed permission for labels
        tempStream.getTracks().forEach(track => track.stop());
      } catch (permError) {
        // Permission denied or not available - continue anyway
        console.log('Could not get mic permission for device listing:', permError);
      }

      // Now enumerate devices (should have proper labels if permission was granted)
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioInputs = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          kind: 'audioinput' as const
        }));

      const audioOutputs = devices
        .filter(device => device.kind === 'audiooutput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.slice(0, 8)}`,
          kind: 'audiooutput' as const
        }));

      setAudioDevices(audioInputs);
      setOutputDevices(audioOutputs);

      // Set the first device as selected if none is selected
      if (audioInputs.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(audioInputs[0].deviceId);
      }
      if (audioOutputs.length > 0 && !selectedOutputDeviceId) {
        setSelectedOutputDeviceId(audioOutputs[0].deviceId);
      }
    } catch (error) {
      console.error('Failed to fetch audio devices:', error);
    }
  }, [selectedDeviceId, selectedOutputDeviceId]);


  // Fetch devices only when connected (after permission is granted)
  useEffect(() => {
    if (isConnected && audioDevices.length === 0 && outputDevices.length === 0) {
      // Only fetch devices when connected (permission already granted)
      fetchAudioDevices();
    }
  }, [isConnected, audioDevices.length, outputDevices.length, fetchAudioDevices]);

  // Clear devices when widget closes
  useEffect(() => {
    if (widgetMode === 'collapsed') {
      setAudioDevices([]);
      setOutputDevices([]);
      setSelectedDeviceId('');
      setSelectedOutputDeviceId('');
    }
  }, [widgetMode]);

  // Listen for device changes only when widget is open
  useEffect(() => {
    if (widgetMode !== 'collapsed' && navigator?.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', fetchAudioDevices);

      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', fetchAudioDevices);
      };
    }
  }, [widgetMode, fetchAudioDevices]);

  return (
    <>
      {/* Overlay when widget is open - only show if not inline mode */}
      {!embedded && !inlineMode && widgetMode !== 'collapsed' && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 backdrop-blur-sm"
        />
      )}

      {/* Widget */}
      <div
        className={inlineMode
          ? `flex w-full h-full ${className} font-Nunito [&_*]:font-Nunito`
          : isMobile && widgetMode === 'collapsed'
            ? `fixed ${positionClasses} z-50 ${className} font-Nunito [&_*]:font-Nunito`
            : isMobile
              ? `fixed ${positionClasses} z-50 ${className} font-Nunito [&_*]:font-Nunito px-2`
              : `fixed ${positionClasses} z-50 ${className} font-Nunito [&_*]:font-Nunito`
        }
        dir={direction}
        lang={language}
      >
        {widgetMode === 'collapsed' && !hideLauncherButton ? (
          // Collapsed state - show main button
          <button
            type="button"
            onClick={handleCallClick}
            className="w-16 h-16 flex items-center justify-center rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
            style={{
              background: `linear-gradient(135deg, ${widgetTheme.config.colors.avatar_first_color} 0%, ${widgetTheme.config.colors.avatar_second_color} 100%)`,
              borderRadius: `${widgetTheme.config.spacing.button_radius}px`,
            }}
          >
            <Phone
              className="h-6 w-6"
              style={{ color: widgetTheme.config.colors.accent_primary }}
            />
          </button>
        ) : widgetMode === 'mic-error' ? (
          // Mic error full screen panel
          <div style={{ backgroundColor: widgetTheme.config.colors.base, borderColor: widgetTheme.config.colors.base_border }} className={isMobile ? 'rounded-xl p-4 w-full mx-auto shadow-[0_0_12px_0_#00000012]' : 'rounded-xl p-4 w-[460px] shadow-[0_0_12px_0_#00000012]'}>
            <div className="flex justify-end mb-3">
              <Button
                onClick={handleCollapse}
                variant="ghost"
                size="sm"
                className="text-gray-400 p-1"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center mb-4">
                <Mic className="h-6 w-6 text-white" />
              </div>

              <h3 className="text-red-400 text-lg font-medium mb-2">
                {micErrorShortMessage}
              </h3>
              <p className="text-gray-400 text-sm mb-4 px-4">
                {micErrorLongMessage}
              </p>

              {micPermissionDenied && (
                <div className="flex gap-3">
                  <Button onClick={() => window.location.reload()} className="bg-gray-600 hover:bg-gray-700 text-white" size="sm">
                    {widgetTheme.textContent.refresh_button_label}
                  </Button>
                </div>
              )}
            </div>

          </div>
        ) : widgetMode === 'options' ? (
          // Options state
          <div
            className={`w-full ${inlineMode ? 'h-full flex flex-col min-h-0 max-w-full' : 'shadow-[0_0_12px_0_#00000012] min-w-[280px] lg:w-[380px]'}`}
            style={{
              backgroundColor: widgetTheme.config.colors.base,
              borderColor: widgetTheme.config.colors.base_border,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderRadius: `${widgetTheme.config.spacing.sheet_radius}px`,
              padding: `${widgetTheme.config.spacing.overlay_padding}px`,
              fontFamily: widgetTheme.config.typography.font_family,
            }}
          >
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center transform -translate-y-0.5 shadow-md ${micPermissionError ? '' :
                          voiceState === 'listening' ? 'animate-pulse' :
                            voiceState === 'speaking' ? 'animate-pulse' :
                              voiceState === 'thinking' ? 'animate-pulse' :
                                voiceState === 'connecting' ? 'animate-pulse' :
                                  ''
                        }`}
                      style={{
                        background: micPermissionError
                          ? widgetTheme.config.colors.base_error
                          : voiceState === 'listening' || voiceState === 'speaking' || voiceState === 'thinking' || voiceState === 'connecting'
                            ? widgetTheme.config.colors.accent
                            : `linear-gradient(135deg, ${widgetTheme.config.colors.avatar_first_color} 0%, ${widgetTheme.config.colors.avatar_second_color} 100%)`,
                        borderRadius: `${widgetTheme.config.spacing.button_radius}px`,
                        color: widgetTheme.config.colors.accent_primary,
                      }}
                    >
                      <Phone className="h-4 w-4" style={{ color: widgetTheme.config.colors.accent_primary }} />
                    </div>
                    {(micPermissionError || voiceState === 'listening' || voiceState === 'speaking' || voiceState === 'thinking') && (
                      <div className={`absolute inset-0 rounded-full ${micPermissionError ? "" : "animate-ping"} ${micPermissionError ? 'bg-red-400' :
                          voiceState === 'listening' ? 'bg-green-400' :
                            voiceState === 'speaking' ? 'bg-blue-400' :
                              'bg-yellow-400'
                        } opacity-30`}></div>
                    )}
                  </div>
                  <div
                    className="rounded-lg px-3 py-1"
                    style={{
                      backgroundColor: widgetTheme.config.colors.base_hover,
                      color: widgetTheme.config.colors.accent_primary ?? widgetTheme.config.colors.base_primary,
                      borderRadius: `${widgetTheme.config.spacing.bubble_radius}px`,
                      fontSize: `${widgetTheme.config.typography.font_size_small}px`,
                    }}
                  >
                    <span
                      className="flex items-center"
                      style={{ color: widgetTheme.config.colors.accent_primary ?? widgetTheme.config.colors.base_primary }}
                    >
                      {getStatusText()}
                    </span>
                  </div>
                </div>
                {/* Right: close button */}
                {voiceState !== 'connecting' && (widgetTheme?.config?.options?.show_close_button !== false) && (
                  <Button
                    onClick={handleCollapse}
                    variant="ghost"
                    size="sm"
                    className="p-1"
                    style={{
                      color: widgetTheme.config.colors.base_subtle,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = widgetTheme.config.colors.base_primary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = widgetTheme.config.colors.base_subtle;
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Voice/Gender Selector - Show when NOT connected, dropdown is available, and no agent was initially provided */}
              {!isConnected && voiceState !== 'connecting' && agentChoices.length > 0 && !wasAgentProvidedInitially && (
                <div className="mb-3">
                  <label className="text-xs mb-1.5 flex items-center gap-1.5" style={{ color: widgetTheme.config.colors.base_subtle }}>
                    <Phone className="h-3 w-3" />
                    <span className="font-medium" style={{ fontWeight: widgetTheme.config.typography.font_weight_medium }}>
                      Voice Selection
                    </span>
                  </label>
                  <Select
                    value={currentAgentId}
                    onValueChange={handleAgentSelection}
                  >
                    <SelectTrigger
                      className="text-sm h-9 w-full"
                      style={{
                        backgroundColor: widgetTheme.config.colors.base,
                        borderColor: widgetTheme.config.colors.base_border,
                        color: widgetTheme.config.colors.base_primary,
                        borderRadius: `${widgetTheme.config.spacing.input_radius}px`,
                        fontSize: `${widgetTheme.config.typography.font_size_small}px`,
                      }}
                    >
                      <span className="block truncate text-left">
                        {agentChoices.find(choice => choice.id === currentAgentId)?.label || 'Select Voice'}
                      </span>
                    </SelectTrigger>
                    <SelectContent
                      className="max-h-[200px]"
                      style={{
                        backgroundColor: widgetTheme.config.colors.base,
                        borderColor: widgetTheme.config.colors.base_border,
                        borderRadius: `${widgetTheme.config.spacing.dropdown_sheet_radius}px`,
                      }}
                    >
                      {agentChoices.map((choice) => (
                        <SelectItem
                          key={choice.id}
                          value={choice.id}
                          style={{
                            color: widgetTheme.config.colors.base_primary,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = widgetTheme.config.colors.base_hover;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="flex h-6 w-6 items-center justify-center rounded-lg text-xs font-semibold"
                              style={{
                                background: `linear-gradient(135deg, ${widgetTheme.config.colors.accent} 0%, ${widgetTheme.config.colors.accent_hover || widgetTheme.config.colors.accent} 100%)`,
                                color: widgetTheme.config.colors.accent_primary || '#ffffff',
                              }}
                            >
                              {choice.badge}
                            </div>
                            <span>{choice.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!currentAgentId && (
                    <p className="text-xs mt-1" style={{ color: widgetTheme.config.colors.base_subtle }}>
                      Please select a voice before starting the call
                    </p>
                  )}
                </div>
              )}

              {/* Audio Device Selectors - Show when devices are available */}
              {(audioDevices.length > 0 || outputDevices.length > 0) && voiceState !== 'connecting' && (
                <div className="mb-2 grid grid-cols-2 gap-3">
                  {/* Input Device Selector */}
                  {audioDevices.length > 0 && (
                    <div>
                      <label className="text-xs mb-1.5 flex items-center gap-1.5" style={{ color: widgetTheme.config.colors.base_subtle }}>
                        <Mic className="h-3 w-3" />
                        <span className="font-medium" style={{ fontWeight: widgetTheme.config.typography.font_weight_medium }}>
                          {isConnected
                            ? widgetTheme.textContent.input_device_label_connected
                            : widgetTheme.textContent.input_device_label}
                        </span>
                      </label>
                      <Select
                        value={selectedDeviceId}
                        onValueChange={async (deviceId) => {
                          setSelectedDeviceId(deviceId);

                          // If connected during voice mode, use SDK method to change device
                          if (isConnected) {
                            try {
                              const sdk: any = conversation;
                              if (sdk && typeof sdk.changeInputDevice === 'function') {
                                await sdk.changeInputDevice({
                                  sampleRate: 44100,
                                  format: 'pcm',
                                  preferHeadphonesForIosDevices: true,
                                  inputDeviceId: deviceId,
                                });
                                toast.success(
                                  widgetTheme.textContent.input_device_changed_title,
                                  widgetTheme.textContent.input_device_changed_message
                                );
                              }
                            } catch (error: any) {
                              console.error('Failed to change input device:', error);
                              toast.error(
                                widgetTheme.textContent.input_device_change_failed_title,
                                error.message || widgetTheme.textContent.input_device_change_failed_message
                              );
                            }
                          }
                        }}
                      >
                        <SelectTrigger
                          className="text-sm h-9 w-full"
                          style={{
                            backgroundColor: widgetTheme.config.colors.base,
                            borderColor: widgetTheme.config.colors.base_border,
                            color: widgetTheme.config.colors.base_primary,
                            borderRadius: `${widgetTheme.config.spacing.input_radius}px`,
                            fontSize: `${widgetTheme.config.typography.font_size_small}px`,
                          }}
                        >
                          <span className="block truncate text-left">
                            {audioDevices.find(d => d.deviceId === selectedDeviceId)?.label || widgetTheme.textContent.select_device_placeholder}
                          </span>
                        </SelectTrigger>
                        <SelectContent
                          className="max-h-[200px]"
                          style={{
                            backgroundColor: widgetTheme.config.colors.base,
                            borderColor: widgetTheme.config.colors.base_border,
                            borderRadius: `${widgetTheme.config.spacing.dropdown_sheet_radius}px`,
                          }}
                        >
                          {audioDevices.map((device) => (
                            <SelectItem
                              key={device.deviceId}
                              value={device.deviceId}
                              style={{
                                color: widgetTheme.config.colors.base_primary,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = widgetTheme.config.colors.base_hover;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {device.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Output Device Selector */}
                  {outputDevices.length > 0 && (
                    <div>
                      <label className="text-xs mb-1.5 flex items-center gap-1.5" style={{ color: widgetTheme.config.colors.base_subtle }}>
                        <Volume2 className="h-3 w-3" />
                        <span className="font-medium" style={{ fontWeight: widgetTheme.config.typography.font_weight_medium }}>
                          {isConnected
                            ? widgetTheme.textContent.output_device_label_connected
                            : widgetTheme.textContent.output_device_label}
                        </span>
                      </label>
                      <Select
                        value={selectedOutputDeviceId}
                        onValueChange={async (deviceId) => {
                          setSelectedOutputDeviceId(deviceId);

                          // If connected during voice mode, use SDK method to change device
                          if (isConnected) {
                            try {
                              const sdk: any = conversation;
                              if (sdk && typeof sdk.changeOutputDevice === 'function') {
                                await sdk.changeOutputDevice({
                                  sampleRate: 44100,
                                  format: 'pcm',
                                  outputDeviceId: deviceId,
                                });
                                toast.success(
                                  widgetTheme.textContent.output_device_changed_title,
                                  widgetTheme.textContent.output_device_changed_message
                                );
                              }
                            } catch (error: any) {
                              console.error('Failed to change output device:', error);
                              toast.error(
                                widgetTheme.textContent.output_device_change_failed_title,
                                error.message || widgetTheme.textContent.output_device_change_failed_message
                              );
                            }
                          }
                        }}
                      >
                        <SelectTrigger
                          className="text-sm h-9 w-full"
                          style={{
                            backgroundColor: widgetTheme.config.colors.base,
                            borderColor: widgetTheme.config.colors.base_border,
                            color: widgetTheme.config.colors.base_primary,
                            borderRadius: `${widgetTheme.config.spacing.input_radius}px`,
                            fontSize: `${widgetTheme.config.typography.font_size_small}px`,
                          }}
                        >
                          <span className="block truncate text-left">
                            {outputDevices.find(d => d.deviceId === selectedOutputDeviceId)?.label || widgetTheme.textContent.select_device_placeholder}
                          </span>
                        </SelectTrigger>
                        <SelectContent
                          className="max-h-[200px]"
                          style={{
                            backgroundColor: widgetTheme.config.colors.base,
                            borderColor: widgetTheme.config.colors.base_border,
                            borderRadius: `${widgetTheme.config.spacing.dropdown_sheet_radius}px`,
                          }}
                        >
                          {outputDevices.map((device) => (
                            <SelectItem
                              key={device.deviceId}
                              value={device.deviceId}
                              style={{
                                color: widgetTheme.config.colors.base_primary,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = widgetTheme.config.colors.base_hover;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {device.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Messages */}
              <div
                ref={voiceMessagesRef}
                className={inlineMode
                  ? 'flex-1 overflow-y-auto space-y-2 mb-2 min-h-0 scrollbar-hide'
                  : isMobile
                    ? (voiceState === 'connecting' ? 'flex items-center justify-center h-[44vh] space-y-0 mb-2' : 'min-h-[200px] max-h-[60vh] overflow-y-auto space-y-2 mb-2 scrollbar-hide')
                    : 'min-h-[280px] max-h-80 overflow-y-auto space-y-2 mb-2 scrollbar-hide'
                }
                style={isRTL ? { direction: 'ltr' } : undefined}
              >
                {voiceState === 'connecting' ? (
                  <div className="text-center py-8">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                      {/* Outer rotating ring */}
                      <div
                        className="absolute inset-0 rounded-full border-4"
                        style={{ borderColor: widgetTheme.config.colors.base_border }}
                      ></div>
                      <div
                        className="absolute inset-0 rounded-full border-4 border-transparent animate-spin"
                        style={{ borderTopColor: widgetTheme.config.colors.accent }}
                      ></div>

                      {/* Inner pulsing circle */}
                      <div
                        className="absolute inset-2 rounded-full animate-pulse flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${widgetTheme.config.colors.accent} 0%, ${widgetTheme.config.colors.accent_hover} 100%)`,
                        }}
                      >
                        <Phone className="h-6 w-6" style={{ color: widgetTheme.config.colors.accent_primary }} />
                      </div>

                      {/* Ripple effect */}
                      <div
                        className="absolute inset-0 rounded-full opacity-20 animate-ping"
                        style={{ backgroundColor: widgetTheme.config.colors.accent }}
                      ></div>
                    </div>
                    <p className="text-sm font-medium mb-1" style={{ color: widgetTheme.config.colors.accent }}>
                      {widgetTheme.textContent.connecting_status}
                    </p>
                    <p className="text-xs" style={{ color: widgetTheme.config.colors.base_subtle }}>
                      {widgetTheme.textContent.connecting_status}...
                    </p>
                  </div>
                ) : micPermissionError ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center mx-auto mb-3">
                      <Mic className="h-6 w-6 text-white" />
                    </div>
                    <p className="text-red-400 text-sm mb-2">
                      {micErrorShortMessage}
                    </p>
                    <p className="text-gray-400 text-xs mb-4">
                      {micErrorLongMessage}
                    </p>
                    <div className="flex gap-2 justify-center">
                      {micPermissionDenied && (
                        <Button
                          onClick={() => window.location.reload()}
                          className="bg-gray-600 hover:bg-gray-700 text-white"
                          size="sm"
                        >
                          {widgetTheme.textContent.refresh_button_label}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                      style={{
                        background: `linear-gradient(135deg, ${widgetTheme.config.colors.avatar_first_color} 0%, ${widgetTheme.config.colors.avatar_second_color} 100%)`,
                        borderRadius: `${widgetTheme.config.spacing.button_radius}px`,
                      }}
                    >
                      <Phone
                        className="h-6 w-6"
                        style={{ color: widgetTheme.config.colors.accent_primary }}
                      />
                    </div>
                    <p className="text-sm" style={{ color: widgetTheme.config.colors.base_subtle }}>
                      {greetingMessage}
                    </p>
                  </div>
                ) : (
                  messages.map((message, index: number) => {
                    const resolvedText = resolveMessageText(message);
                    return (
                      <div
                        key={index}
                        className={`flex ${message.source === 'user' ? 'justify-end' : message.source === 'system' ? 'justify-center' : 'justify-start'}`}
                      >
                        <div
                          className="max-w-[80%] p-3 rounded-lg text-sm whitespace-pre-wrap"
                          style={{
                            backgroundColor: message.source === 'user'
                              ? widgetTheme.config.colors.accent
                              : message.source === 'system'
                                ? widgetTheme.config.colors.base_hover
                                : widgetTheme.config.colors.base_active,
                            color: message.source === 'user'
                              ? widgetTheme.config.colors.accent_primary
                              : message.source === 'system'
                                ? (widgetTheme.config.colors.accent_primary ?? widgetTheme.config.colors.base_primary)
                                : widgetTheme.config.colors.base_primary,
                            borderRadius: `${widgetTheme.config.spacing.bubble_radius}px`,
                            fontSize: `${widgetTheme.config.typography.font_size_medium}px`,
                            textAlign: message.source === 'system' ? 'center' : 'left',
                          }}
                          dir={direction}
                          lang={language}
                        >
                          {parseMessageWithLinks(resolvedText)}
                          {message.source === 'system' && message.messageKey && conversationId && (
                            <div className="text-xs mt-1 opacity-70" style={{ color: widgetTheme.config.colors.base_primary }}>
                              {widgetTheme.textContent.conversation_id}: {conversationId}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Message input - hide during connecting */}
              {voiceState !== 'connecting' && (
                <div
                  className="flex items-center gap-2"
                >
                  <Input
                    value={chatMessage}
                    onChange={(e) => {
                      setChatMessage(e.target.value);
                      // Inform the voice agent that the user is active (debounced)
                      handleUserActivity();
                    }}
                    placeholder={widgetTheme.textContent.input_placeholder}
                    className="flex-1 input-field-chat"
                    style={{
                      backgroundColor: widgetTheme.config.colors.base,
                      borderColor: widgetTheme.config.colors.base_border,
                      color: widgetTheme.config.colors.base_primary,
                      borderRadius: `${widgetTheme.config.spacing.input_radius}px`,
                      fontSize: `${widgetTheme.config.typography.font_size_medium}px`,
                    }}
                    dir={direction}
                    lang={language}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSendMessage();
                      }
                    }}
                    onFocus={() => {
                      // When user focuses the input, immediately notify the agent and try to stop any speaking
                      handleUserActivity(false);
                    }}
                  />
                  {/* Send typed message without ending the call */}
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={!chatMessage.trim()}
                    style={{
                      backgroundColor: widgetTheme.config.colors.accent,
                      color: widgetTheme.config.colors.accent_primary,
                      borderRadius: `${widgetTheme.config.spacing.button_radius}px`,
                      cursor: !chatMessage.trim() ? 'not-allowed' : 'pointer',
                      opacity: !chatMessage.trim() ? 0.4 : 1,
                      padding: '0.5rem',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: `${widgetTheme.config.typography.font_size_small}px`,
                      fontWeight: widgetTheme.config.typography.font_weight_medium,
                      transition: 'all 0.2s ease',
                      minWidth: '2.5rem',
                      minHeight: '2.5rem',
                    }}
                    onMouseEnter={(e) => {
                      if (chatMessage.trim() && !e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor = widgetTheme.config.colors.accent_hover;
                        e.currentTarget.style.opacity = '1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = widgetTheme.config.colors.accent;
                      e.currentTarget.style.opacity = !chatMessage.trim() ? '0.4' : '1';
                    }}
                  >
                    <Send className="h-4 w-4" style={{ color: widgetTheme.config.colors.accent_primary }} />
                  </button>
                  <button
                    type="button"
                    onClick={voiceState === 'idle' ? handleCallClick : endConversation}
                    disabled={isCallDisabled}
                    style={{
                      backgroundColor: widgetTheme.config.colors.accent,
                      color: widgetTheme.config.colors.accent_primary,
                      borderRadius: `${widgetTheme.config.spacing.button_radius}px`,
                      padding: '0.5rem 0.75rem',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: `${widgetTheme.config.typography.font_size_small}px`,
                      fontWeight: widgetTheme.config.typography.font_weight_medium,
                      cursor: isCallDisabled ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s ease',
                      minHeight: '2.5rem',
                      opacity: isCallDisabled ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!isCallDisabled) {
                        e.currentTarget.style.backgroundColor = widgetTheme.config.colors.accent_hover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = widgetTheme.config.colors.accent;
                    }}
                  >
                    <Phone className="h-4 w-4 mr-2" style={{ color: widgetTheme.config.colors.accent_primary }} />
                    <span style={{ color: widgetTheme.config.colors.accent_primary }}>
                      {voiceState === 'idle' ? widgetTheme.textContent.new_call : widgetTheme.textContent.end_call}
                    </span>
                  </button>
                </div>
              )}
            </>
          </div>
        ) : null}
      </div>
    </>
  );
});

FloatingVoiceAgentComponent.displayName = 'FloatingVoiceAgent';

export const FloatingVoiceAgent = FloatingVoiceAgentComponent;